import { useCallback } from 'react';

import { apiFetchUserByEmail } from 'api/apiFetchUserByEmail';
import { type ListingApplicationPayload, validateApplication } from 'api/ats';
import type { ApiListingApplicationListingType } from 'api/types/ApiListingApplicationListingType';
import type { FormSubmitFunctionData } from 'components/Form/FormContext';
import { Text } from 'components/Text/Text';
import { useTemporaryStorageContext } from 'containers/TemporaryStorageContext';
import { useI18n } from 'i18n/hooks/useI18n';
import { LISTING_TYPE_HUMAN_NAME_MAP } from 'utils/constants/general/listingTypeHumanNameMap';
import { showModal } from 'zustand-stores/modalStore';

type Args = {
  listingType: ApiListingApplicationListingType;
};

export function useListingApplicationSubmissionAsGuest({ listingType }: Args) {
  const { i18n } = useI18n();
  const { saveTemporaryStorage } = useTemporaryStorageContext();

  const submitListingApplicationAsGuest = useCallback(
    async ({
      values,
      updateSubmitting,
    }: FormSubmitFunctionData<ListingApplicationPayload>) => {
      await validateApplication(values);

      const [id, userExists] = await Promise.all([
        saveTemporaryStorage?.([
          { type: 'LISTING_APPLICATION_VALUES', data: values },
        ]),
        values.email && apiFetchUserByEmail(values.email),
      ]);

      updateSubmitting(false);
      const redirectTo = `${window.location.pathname}${
        window.location.search
      }${window.location.search ? '&' : '?'}tmpId=${id ?? ''}`;

      if (userExists) {
        showModal('LOGIN', { redirectTo, email: values.email });
        return;
      }

      showModal('SIGNUP', {
        title: i18n('Just One More Step!'),
        description: (
          <Text mb={4}>
            {i18n(
              'Create an account on Idealist in order to finish applying to this %(listingType)s.',
              { listingType: LISTING_TYPE_HUMAN_NAME_MAP(i18n)[listingType] },
            )}
          </Text>
        ),
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        redirectTo,
      });
    },
    [i18n, listingType, saveTemporaryStorage],
  );

  return {
    submitListingApplicationAsGuest,
  };
}
